import { productApi } from "#/api/productApi";
import SelectFile from "#/components/inputs/SelectFile";
import {
  showSuccessNotification,
  showErrorNotification,
} from "#/components/notifications";
import { logger } from "#/utils/logger";
import type { ModalProps } from "@mantine/core";
import {
  Button,
  Divider,
  Drawer,
  Modal,
  Progress as ProgressBar,
  Table,
  Text,
  TextInput,
  Title,
  Badge,
} from "@mantine/core";
import {
  IconDownload,
  IconUpload,
  IconRefresh,
  IconCheck,
  IconX,
  IconEye,
  IconTrash,
} from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import ActionButton from "#/components/buttons/ActionButton";
import BaseTable from "#/components/tables/BaseTable";
import type { Product } from "#/types/products";
import Papa from "papaparse";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";

interface CsvError {
  row: number;
  data: Record<string, string>;
  error: string;
}

interface ImportResult {
  success: Product[];
  errors: CsvError[];
}

interface PreviewRow {
  rowIndex: number;
  data: Record<string, string>;
}

export interface Props extends ModalProps {
  onSuccess: () => void;
}

const CSV_COLUMNS = [
  { key: "sku", label: "SKU" },
  { key: "name", label: "Name" },
  { key: "variant", label: "Type" },
  { key: "cost", label: "Cost" },
  { key: "retail_price", label: "Retail Price" },
  { key: "filling_capacity", label: "Filling Capacity" },
  { key: "tare_weight", label: "Tare Weight" },
  { key: "description", label: "Description" },
];

const ImportCSVModal = ({ onSuccess, onClose, opened }: Props) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const allowedStatuses = useAllowedStatuses();
  const [processing, setProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [editableErrors, setEditableErrors] = useState<CsvError[]>([]);
  const [retrying, setRetrying] = useState<boolean>(false);

  // Preview state
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [customColumns, setCustomColumns] = useState<string[]>([]);
  const [scanStepColumns, setScanStepColumns] = useState<string[]>([]);

  const KNOWN_COLUMNS = [
    "SKU",
    "Name",
    "Type",
    "Cost",
    "Retail Price",
    "Filling Capacity",
    "Tare Weight",
    "Description",
  ];

  const SCAN_STEP_REGEX = /^Scan Step (\d+)$/i;

  const parseCSV = (csvFile: File): Promise<PreviewRow[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse(csvFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const rows: PreviewRow[] = [];

          // Detect all headers
          const allHeaders = results.meta.fields || [];

          // Identify scan step columns
          const detectedScanSteps = allHeaders.filter((header) =>
            SCAN_STEP_REGEX.test(header)
          );
          setScanStepColumns(detectedScanSteps);

          // Identify custom columns (not in KNOWN_COLUMNS and not scan steps)
          const detectedCustomColumns = allHeaders.filter(
            (header) =>
              !KNOWN_COLUMNS.includes(header) && !SCAN_STEP_REGEX.test(header)
          );
          setCustomColumns(detectedCustomColumns);

          results.data.forEach((row: unknown, index: number) => {
            const record = row as Record<string, string>;
            const mappedData: Record<string, string> = {
              sku: record["SKU"]?.trim() || "",
              name: record["Name"]?.trim() || "",
              variant: record["Type"]?.trim() || "",
              cost: record["Cost"]?.trim() || "",
              retail_price: record["Retail Price"]?.trim() || "",
              filling_capacity: record["Filling Capacity"]?.trim() || "",
              tare_weight: record["Tare Weight"]?.trim() || "",
              description: record["Description"]?.trim() || "",
            };

            // Add scan step columns (only non-empty values)
            detectedScanSteps.forEach((scanStepCol) => {
              const value = record[scanStepCol]?.trim() || "";
              if (value) {
                mappedData[scanStepCol] = value;
              }
            });

            // Add custom columns (only non-empty values)
            detectedCustomColumns.forEach((customCol) => {
              const value = record[customCol]?.trim() || "";
              if (value) {
                mappedData[customCol] = value;
              }
            });

            const hasData = Object.values(mappedData).some(
              (v) => v && v.length > 0,
            );
            if (hasData) {
              rows.push({ rowIndex: index + 2, data: mappedData });
            }
          });
          resolve(rows);
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  };

  const handleFileSelect = async (selectedFile: File | null) => {
    if (selectedFile) {
      try {
        const rows = await parseCSV(selectedFile);
        setPreviewData(rows);
        if (rows.length > 0) {
          setShowPreview(true);
        } else {
          showErrorNotification({
            message: t("products.import.noValidData"),
          });
        }
      } catch {
        showErrorNotification({
          message: t("products.import.failedToParse"),
        });
      }
    }
  };

  const handlePreviewFieldChange = (
    rowIndex: number,
    field: string,
    value: string,
  ) => {
    setPreviewData((prev) =>
      prev.map((row, idx) =>
        idx === rowIndex
          ? { ...row, data: { ...row.data, [field]: value } }
          : row,
      ),
    );
  };

  const handleRemovePreviewRow = (rowIndex: number) => {
    setPreviewData((prev) => prev.filter((_, idx) => idx !== rowIndex));
  };

  const processData = async () => {
    if (previewData.length === 0) return;

    setProcessing(true);
    setResult(null);

    const allHeaders = [
      ...CSV_COLUMNS.map((col) => col.label),
      ...scanStepColumns,
      ...customColumns,
    ];
    const csvHeader = allHeaders.join(",");
    const csvRows = previewData.map((row) => {
      const knownFields = CSV_COLUMNS.map((col) => row.data[col.key] || "");
      const scanStepFields = scanStepColumns.map(
        (scanStepCol) => row.data[scanStepCol] || ""
      );
      const customFields = customColumns.map(
        (customCol) => row.data[customCol] || ""
      );
      return [...knownFields, ...scanStepFields, ...customFields].join(",");
    });
    const csvContent = [csvHeader, ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const csvFile = new File([blob], "import.csv", { type: "text/csv" });

    try {
      const response = await productApi.uploadCsvFile(csvFile, setProgress);
      setProcessing(false);
      setProgress(0);

      const data = response.data;
      setResult(data);
      setEditableErrors(
        data.errors.map((e) => ({ ...e, data: { ...e.data } })),
      );

      if (data.success.length > 0 && data.errors.length === 0) {
        showSuccessNotification({
          message: t("products.import.importSuccess", { count: data.success.length }),
        });
        onSuccess();
      } else if (data.success.length > 0 && data.errors.length > 0) {
        showSuccessNotification({
          message: t("products.import.importPartial", { success: data.success.length, errors: data.errors.length }),
        });
        onSuccess();
      } else if (data.success.length === 0 && data.errors.length > 0) {
        showErrorNotification({
          message: t("products.import.allFailed", { count: data.errors.length }),
        });
      } else {
        showErrorNotification({
          message: t("products.import.noValidDataFound"),
        });
      }
    } catch (error) {
      logger.error("Process error:", error);
      showErrorNotification({
        message: t("products.import.failedToProcessData"),
      });
      setProcessing(false);
      setProgress(0);
    }
  };

  const handleRetry = async () => {
    if (editableErrors.length === 0) return;

    setRetrying(true);

    const allHeaders = [
      ...CSV_COLUMNS.map((col) => col.label),
      ...scanStepColumns,
      ...customColumns,
    ];
    const csvHeader = allHeaders.join(",");
    const csvRows = editableErrors.map((err) => {
      const knownFields = CSV_COLUMNS.map((col) => err.data[col.key] || "");
      const scanStepFields = scanStepColumns.map(
        (scanStepCol) => err.data[scanStepCol] || ""
      );
      const customFields = customColumns.map(
        (customCol) => err.data[customCol] || ""
      );
      return [...knownFields, ...scanStepFields, ...customFields].join(",");
    });
    const csvContent = [csvHeader, ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const retryFile = new File([blob], "retry.csv", { type: "text/csv" });

    try {
      const response = await productApi.uploadCsvFile(retryFile, setProgress);
      const data = response.data;

      setResult((prev) => ({
        success: [...(prev?.success || []), ...data.success],
        errors: data.errors,
      }));
      setEditableErrors(
        data.errors.map((e) => ({ ...e, data: { ...e.data } })),
      );

      if (data.success.length > 0) {
        showSuccessNotification({
          message: t("products.import.moreImported", { count: data.success.length }),
        });
        onSuccess();
      }

      if (data.errors.length === 0) {
        showSuccessNotification({
          message: t("products.import.allImported"),
        });
      } else if (data.success.length === 0) {
        showErrorNotification({
          message: t("products.import.retryFailed"),
        });
      }
    } catch (error) {
      logger.error("Retry error:", error);
      showErrorNotification({
        message: t("products.import.failedToRetry"),
      });
    } finally {
      setRetrying(false);
      setProgress(0);
    }
  };

  const handleErrorFieldChange = (
    errorIndex: number,
    field: string,
    value: string,
  ) => {
    setEditableErrors((prev) =>
      prev.map((err, idx) =>
        idx === errorIndex
          ? { ...err, data: { ...err.data, [field]: value } }
          : err,
      ),
    );
  };

  const handleDownloadTemplate = useCallback(() => {
    const csvLink = document.createElement("a");
    csvLink.href = "/templates/categories_template.csv";
    csvLink.download = "categories_template.csv";
    csvLink.click();

    setTimeout(() => {
      // Generate instructions dynamically
      const instructionsContent = `===============================================================================
                       TMA CATEGORIES IMPORT INSTRUCTIONS
===============================================================================

This guide explains how to use the categories_template.csv file to bulk import
categories into TMA.


GETTING STARTED
-------------------------------------------------------------------------------
1. Open categories_template.csv in your preferred spreadsheet application
2. The first row contains column headers - do not modify it
3. Row 2 contains an example - you can delete or replace it
4. Add your data starting from row 2 or below


REQUIRED FIELDS
-------------------------------------------------------------------------------
SKU
    The unique SKU code for the category.
    Must be unique across all categories in your organization.

Name
    The name of the category.
    Required field.


OPTIONAL FIELDS
-------------------------------------------------------------------------------
Type
    The type or variant of the category.
    Free text field.

Cost
    The cost price of the category.
    Must be a valid number (e.g., 15.50).

Retail Price
    The retail price of the category.
    Must be a valid number (e.g., 25.00).

Filling Capacity
    The filling capacity (e.g., volume in liters or weight in kg).
    Must be a valid number (e.g., 500).

Tare Weight
    The tare weight (empty weight).
    Must be a valid number (e.g., 50).

Description
    Detailed description of the category.
    Free text field.


SCAN STEPS (OPTIONAL)
-------------------------------------------------------------------------------
You can define scan steps for the category using "Scan Step X" columns,
where X is a step number (e.g., "Scan Step 1", "Scan Step 2", etc.).

The value in each scan step column should be the exact status name.

Your organization's allowed statuses are:
${allowedStatuses.map((status) => `- ${status}`).join("\n")}

Example:
- Scan Step 1: in_stock_main
- Scan Step 2: in_transit
- Scan Step 3: delivered

Notes:
- Scan steps are completely optional
- You can have as many or as few scan steps as needed
- Leave the cell empty if a scan step is not applicable
- Step numbers should be sequential but gaps are allowed


CUSTOM FIELDS (OPTIONAL)
-------------------------------------------------------------------------------
You can add custom fields by adding additional columns after "Description".

Any column added that doesn't match the known fields or "Scan Step X" pattern
will be treated as a custom field.
The column header will be the custom field name.
The cell value will be the custom field value.

Example custom fields in the template:
- Manufacturer
- Batch Number

Notes:
- Custom fields are completely optional
- Each category can have different custom fields
- If a cell is empty, that custom field will not be stored for that category
- You can add as many custom columns as needed


TIPS
-------------------------------------------------------------------------------
- Ensure all SKU codes are unique before importing
- Save your file as CSV (comma-separated) before uploading
- Check for any extra spaces in your data
- Numeric fields (Cost, Retail Price, Filling Capacity, Tare Weight) should
  contain only numbers and decimal points (no currency symbols or units)


NEED HELP?
-------------------------------------------------------------------------------
Contact your system administrator if you encounter any issues during import.

===============================================================================
`;

      const blob = new Blob([instructionsContent], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const instructionsLink = document.createElement("a");
      instructionsLink.href = url;
      instructionsLink.download = "categories_import_instructions.txt";
      instructionsLink.click();
      URL.revokeObjectURL(url);
    }, 100);
  }, [allowedStatuses]);

  const handleClose = () => {
    setResult(null);
    setEditableErrors([]);
    setProgress(0);
    setPreviewData([]);
    setShowPreview(false);
    onClose();
  };

  const resetUpload = () => {
    setResult(null);
    setEditableErrors([]);
    setProgress(0);
    setPreviewData([]);
    setShowPreview(false);
  };

  // Build preview table rows
  const previewHeaders = [
    ...CSV_COLUMNS.map((col) => col.label),
    ...scanStepColumns,
    ...customColumns,
    t("common.actions"),
  ];
  const previewRows = previewData.map((row, idx) => (
    <Table.Tr key={idx}>
      {CSV_COLUMNS.map((col) => (
        <Table.Td key={col.key}>
          <TextInput
            size="xs"
            value={row.data[col.key] || ""}
            disabled={processing}
            onChange={(e) =>
              handlePreviewFieldChange(idx, col.key, e.target.value)
            }
          />
        </Table.Td>
      ))}
      {scanStepColumns.map((scanStepCol) => (
        <Table.Td key={scanStepCol}>
          <TextInput
            size="xs"
            value={row.data[scanStepCol] || ""}
            disabled={processing}
            onChange={(e) =>
              handlePreviewFieldChange(idx, scanStepCol, e.target.value)
            }
          />
        </Table.Td>
      ))}
      {customColumns.map((customCol) => (
        <Table.Td key={customCol}>
          <TextInput
            size="xs"
            value={row.data[customCol] || ""}
            disabled={processing}
            onChange={(e) =>
              handlePreviewFieldChange(idx, customCol, e.target.value)
            }
          />
        </Table.Td>
      ))}
      <Table.Td>
        <ActionButton
          icon={IconTrash}
          color="red"
          variant="subtle"
          size="sm"
          onClick={() => handleRemovePreviewRow(idx)}
          disabled={processing}
        />
      </Table.Td>
    </Table.Tr>
  ));

  // Build success table rows
  const successHeaders = [t("common.id"), t("products.form.sku"), t("products.form.name"), t("products.form.type"), t("products.columns.cost"), t("products.columns.retailPrice")];
  const successRows =
    result?.success.map((product) => (
      <Table.Tr key={product.id}>
        <Table.Td>{product.id}</Table.Td>
        <Table.Td>{product.sku || "-"}</Table.Td>
        <Table.Td>{product.name || "-"}</Table.Td>
        <Table.Td>{product.variant || "-"}</Table.Td>
        <Table.Td>{product.cost ? `$${product.cost}` : "-"}</Table.Td>
        <Table.Td>
          {product.retail_price ? `$${product.retail_price}` : "-"}
        </Table.Td>
      </Table.Tr>
    )) || [];

  // Build error table rows
  const errorHeaders = [t("common.error"), ...CSV_COLUMNS.map((col) => col.label)];
  const errorRows = editableErrors.map((err, idx) => (
    <Table.Tr key={idx}>
      <Table.Td>
        <Text size="xs" c="red">
          {err.error}
        </Text>
      </Table.Td>
      {CSV_COLUMNS.map((col) => (
        <Table.Td key={col.key}>
          <TextInput
            size="xs"
            value={err.data[col.key] || ""}
            disabled={retrying}
            onChange={(e) =>
              handleErrorFieldChange(idx, col.key, e.target.value)
            }
          />
        </Table.Td>
      ))}
    </Table.Tr>
  ));

  // Upload screen (Modal)
  if (!showPreview && !result) {
    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        closeOnClickOutside={!processing}
        closeOnEscape={!processing}
        title={t("products.import.title")}
        size="xl"
        centered
      >
        <div className="w-full flex flex-col gap-6">
          <div className="w-full h-96 gap-4 flex flex-col items-center justify-center">
            <div className="w-full grid grid-cols-[1fr_auto] gap-10">
              <div className="w-full flex flex-col">
                <Title order={6}>{t("products.import.downloadTemplateStep")}</Title>
                <Text size="sm" c="gray.6">
                  {t("products.import.downloadTemplateDescription")}
                </Text>
              </div>
              <Button
                color="black"
                variant="outline"
                leftSection={<IconDownload size={16} />}
                onClick={handleDownloadTemplate}
              >
                {t("products.import.downloadTemplate")}
              </Button>
            </div>
            <div className="w-full">
              <Divider color="gray.3" />
            </div>
            <div className="w-full h-full flex flex-col gap-2">
              <Title order={6}>{t("products.import.uploadStep")}</Title>
              <SelectFile
                accept=".csv,text/csv"
                onFileSelect={handleFileSelect}
              />
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  // Preview screen (Bottom Drawer)
  if (showPreview && !result) {
    return (
      <Drawer
        opened={opened}
        onClose={handleClose}
        title={t("products.import.reviewImportData")}
        position="bottom"
        size="90%"
        closeOnClickOutside={!processing}
        closeOnEscape={!processing}
      >
        <div className="h-full flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge color="blue" size="lg" leftSection={<IconEye size={14} />}>
                {t("products.import.rowsToImport", { count: previewData.length })}
              </Badge>
            </div>
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                color="green"
                leftSection={<IconUpload size={16} />}
                onClick={processData}
                loading={processing}
                disabled={previewData.length === 0 || !canWrite()}
              >
                {t("products.import.processImport")}
              </Button>
            </PermissionTooltip>
          </div>

          {processing && (
            <div className="w-full relative">
              <ProgressBar
                color="green.4"
                size="38"
                value={progress}
                autoContrast
              />
              <span className="font-bold uppercase text-xs text-black/50 absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 z-10">
                {`${progress}%`}
              </span>
            </div>
          )}

          <div className="flex-1 overflow-auto">
            <BaseTable
              headers={previewHeaders}
              rows={previewRows}
              loading={processing}
            />
          </div>
        </div>
      </Drawer>
    );
  }

  // Results screen (Bottom Drawer)
  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      title={t("products.import.importResults")}
      position="bottom"
      size="90%"
      closeOnClickOutside={!retrying}
      closeOnEscape={!retrying}
    >
      <div className="h-full flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {result && result.success.length > 0 && (
              <Badge
                color="green"
                size="lg"
                leftSection={<IconCheck size={14} />}
              >
                {t("products.import.imported", { count: result.success.length })}
              </Badge>
            )}
            {editableErrors.length > 0 && (
              <Badge color="red" size="lg" leftSection={<IconX size={14} />}>
                {t("products.import.errors", { count: editableErrors.length })}
              </Badge>
            )}
          </div>
          <Button
            variant="subtle"
            color="gray"
            onClick={resetUpload}
            disabled={retrying}
          >
            {t("products.import.uploadAnotherFile")}
          </Button>
        </div>

        {result && result.success.length > 0 && (
          <div className="flex flex-col gap-2">
            <Title order={5} c="green">
              {t("products.import.successfullyImportedCategories")}
            </Title>
            <BaseTable headers={successHeaders} rows={successRows} />
          </div>
        )}

        {editableErrors.length > 0 && (
          <div className="flex flex-col gap-2 flex-1">
            <div className="flex items-center justify-between">
              <Title order={5} c="red">
                {t("products.import.failedRows")}
              </Title>
              <Button
                color="blue"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRetry}
                loading={retrying}
              >
                {t("products.import.retryFailedRows")}
              </Button>
            </div>
            <div className="flex-1 overflow-auto">
              <BaseTable
                headers={errorHeaders}
                rows={errorRows}
                loading={retrying}
              />
            </div>
          </div>
        )}

        {editableErrors.length === 0 && result && result.success.length > 0 && (
          <div className="flex justify-end">
            <Button color="green" onClick={handleClose}>
              {t("common.done")}
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ImportCSVModal;
